// Reiki Healing Frome - Service Page
import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Clock, MapPin } from "lucide-react";
import Navigation from "../../components/shared/Navigation";
import Footer from "../../components/shared/Footer";
import PageBreadcrumb from "../../components/shared/PageBreadcrumb";
import { usePageMeta, generateServiceSchema, generateFAQSchema, SchemaScript } from "../../components/shared/SEOHelper";

const ReikiHealingFrome = () => {
  usePageMeta({
    title: "Reiki Healing in Frome | Energy Healing Somerset",
    description: "Experience Reiki healing in Frome, Somerset with Reiki Master Practitioner Annelie Lily. Gentle energy work to reduce stress, promote relaxation",
    canonical: "https://annelielily.com/treatments/reiki-healing"
  });

  const serviceSchema = generateServiceSchema({
    name: "Reiki Healing in Frome",
    description: "Reiki energy healing sessions to reduce stress, promote relaxation, and support the body's natural healing ability.",
    serviceType: "Reiki Healing Treatment",
    url: "https://annelielily.com/treatments/reiki-healing",
    image: "https://customer-assets.emergentagent.com/job_e5a39abe-98a9-41c1-8b03-8654ab8086e3/artifacts/63wkoq5d_IMG_3956.jpeg"
  });

  const faqs = [
    {
      question: "What is Reiki?",
      answer: "Reiki is a form of energy healing that originated in Japan. The practitioner channels healing energy through their hands, either lightly touching or hovering above the body, to support relaxation and the body's natural healing processes."
    },
    {
      question: "What does Reiki feel like?",
      answer: "Experiences vary. Most people feel deeply relaxed, sometimes warm or tingly. Some feel almost like floating. Others simply feel peaceful and calm. There's no 'wrong' way to experience Reiki."
    },
    {
      question: "Do I need to believe in energy healing for Reiki to work?",
      answer: "An open mind is helpful, but you don't need any particular beliefs. Many people approach Reiki with curiosity rather than conviction and still find it deeply relaxing and beneficial."
    },
    {
      question: "How long is a Reiki session?",
      answer: "Sessions typically last 45-60 minutes, though this can vary depending on your needs. We'll discuss what feels right when you book."
    }
  ];

  const faqSchema = generateFAQSchema(faqs);

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={serviceSchema} />
      <SchemaScript schema={faqSchema} />
      <Navigation />
      <PageBreadcrumb currentPage="Reiki Healing" currentSlug="treatments/reiki-healing" />
      
      {/* Hero */}
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
                Reiki in Frome
              </span>
              <h1 className="font-heading text-4xl md:text-5xl font-light text-neutral-800 mb-5">
                Reiki Healing
              </h1>
              <p className="text-neutral-700 leading-relaxed mb-6">
                Gentle, non-invasive energy work that supports deep relaxation and the body's 
                natural ability to heal. Whether you're dealing with stress, recovering from 
                illness, or simply want to feel more balanced — Reiki offers a space to rest 
                and restore.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact" className="btn-primary">
                  Book a Treatment <ArrowRight className="w-4 h-4 inline ml-2" />
                </Link>
              </div>
              
              <div className="flex flex-wrap gap-4 mt-6 text-sm text-neutral-600">
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gold" />
                  45-60 minutes
                </span>
                <span className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gold" />
                  Frome, Somerset
                </span>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="aspect-square rounded-2xl overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_e5a39abe-98a9-41c1-8b03-8654ab8086e3/artifacts/63wkoq5d_IMG_3956.jpeg"
                  alt="Annelie Lily performing gentle Reiki energy healing on a client in her Frome studio"
                  className="w-full h-full object-cover"
                  width={600}
                  height={600}
                  loading="eager"
                  decoding="async"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* What Is Reiki */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              How Reiki Works
            </h2>
            <div className="prose-content space-y-4 text-neutral-700">
              <p>
                Reiki is based on the idea that we all have a life force energy flowing through us. 
                When that energy is low or blocked, we're more likely to feel stressed, unwell, or 
                out of balance. When it flows freely, we feel more alive and healthy.
              </p>
              <p>
                During a treatment, you lie fully clothed on a treatment table while I place my 
                hands lightly on or just above different areas of your body. Energy flows through 
                my hands to wherever your body needs it most.
              </p>
              <p>
                There's nothing you need to do except relax. Most people enter a deeply restful 
                state — some fall asleep, others experience vivid images or emotions. Whatever 
                happens is right for you.
              </p>
              <p>
                I'm trained as a <strong>Reiki Master Practitioner</strong>, which means I've 
                completed all three levels of Reiki training and attunement. Reiki pairs beautifully 
                with <Link to="/yoga-classes/yin-yoga" className="text-gold hover:underline">Yin yoga</Link> for those seeking deep relaxation, 
                or with <Link to="/treatments/chakra-healing" className="text-gold hover:underline">chakra healing</Link> for more focused energy work.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Benefits */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6 text-center">
              What Reiki Can Help With
            </h2>
            <div className="bg-white/70 p-6 md:p-8 rounded-xl border border-gold/10">
              <ul className="space-y-4">
                <li className="flex items-start gap-3 text-neutral-700">
                  <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <span><strong>Stress and anxiety</strong> — the deep relaxation of Reiki can help calm an overactive nervous system.</span>
                </li>
                <li className="flex items-start gap-3 text-neutral-700">
                  <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <span><strong>Sleep problems</strong> — many people report improved sleep after Reiki sessions.</span>
                </li>
                <li className="flex items-start gap-3 text-neutral-700">
                  <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <span><strong>Recovery from illness</strong> — supporting the body's natural healing alongside medical treatment.</span>
                </li>
                <li className="flex items-start gap-3 text-neutral-700">
                  <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <span><strong>Emotional processing</strong> — Reiki can help release stored emotions and promote emotional balance.</span>
                </li>
                <li className="flex items-start gap-3 text-neutral-700">
                  <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <span><strong>General wellbeing</strong> — maintaining balance and preventing burnout.</span>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8 text-center">
              Questions About Reiki
            </h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white/70 p-5 rounded-xl border border-gold/10">
                  <h3 className="font-heading text-lg text-neutral-800 mb-2">{faq.question}</h3>
                  <p className="text-neutral-600 text-sm">{faq.answer}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
              Ready to Experience Reiki?
            </h2>
            <p className="text-neutral-600 mb-6 max-w-xl mx-auto">
              Get in touch to book a Reiki treatment. If you're not sure whether Reiki is right 
              for you, I'm happy to chat through what's involved.
            </p>
            <Link to="/contact" className="btn-primary">
              Book a Treatment <ArrowRight className="w-4 h-4 inline ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Related */}
      <section className="py-12 px-6 md:px-12 bg-cream">
        <div className="max-w-6xl mx-auto">
          <h3 className="font-heading text-xl text-neutral-800 mb-6 text-center">Related Treatments</h3>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/treatments/swedish-massage" className="text-sm text-gold hover:underline underline-offset-4">Swedish Massage</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/treatments/chakra-healing" className="text-sm text-gold hover:underline underline-offset-4">Chakra Healing</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/treatments" className="text-sm text-gold hover:underline underline-offset-4">All Treatments</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/yoga-classes/yin-yoga" className="text-sm text-gold hover:underline underline-offset-4">Yin Yoga</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ReikiHealingFrome;
